/**
 * Account Relationships
 *
 * Functions for linking/unlinking parents, teachers, and learners.
 * Part of the accounts domain (split from accountHelpers.ts).
 */

import {
  AccountsState,
  RelationshipGraph,
  isLearnerAccount,
  isParentAccount,
} from '../types'
import { error as logError } from '../../utils/logger'

/** Maximum number of parents that can be linked to a single learner */
export const MAX_PARENTS_PER_LEARNER = 5

/**
 * Link parent to learner (bidirectional)
 * Enforces MAX_PARENTS_PER_LEARNER cap.
 * Returns unchanged state if cap is exceeded.
 */
export function linkParentToLearner(
  accountsState: AccountsState,
  parentId: string,
  learnerId: string,
): AccountsState {
  const parent = accountsState.accountsById[parentId]
  const learner = accountsState.accountsById[learnerId]
  if (!parent || !isParentAccount(parent)) {
    logError(`Invalid parent account: ${parentId}`)
    return accountsState
  }
  if (!learner || !isLearnerAccount(learner)) {
    logError(`Invalid learner account: ${learnerId}`)
    return accountsState
  }

  // Check if already linked (dedup)
  const existingParents =
    accountsState.relationships.learnerToParents[learnerId] || []
  if (existingParents.includes(parentId)) {
    return accountsState // Already linked, no-op
  }

  // Enforce max parents cap
  if (existingParents.length >= MAX_PARENTS_PER_LEARNER) {
    logError(
      `Cannot link parent to learner ${learnerId}: max ${MAX_PARENTS_PER_LEARNER} parents reached`,
    )
    return accountsState
  }

  const newRelationships: RelationshipGraph = {
    ...accountsState.relationships,
    parentToLearners: {
      ...accountsState.relationships.parentToLearners,
      [parentId]: [
        ...(accountsState.relationships.parentToLearners[parentId] || []),
        learnerId,
      ].filter((id, index, self) => self.indexOf(id) === index),
    },
    learnerToParents: {
      ...accountsState.relationships.learnerToParents,
      [learnerId]: [...existingParents, parentId].filter(
        (id, index, self) => self.indexOf(id) === index,
      ),
    },
  }
  return {
    ...accountsState,
    relationships: newRelationships,
  }
}

/**
 * Link teacher to learner
 */
export function linkTeacherToLearner(
  accountsState: AccountsState,
  teacherId: string,
  learnerId: string,
): AccountsState {
  const teacher = accountsState.accountsById[teacherId]
  const learner = accountsState.accountsById[learnerId]
  if (!teacher || teacher.role !== 'teacher') {
    logError(`Invalid teacher account: ${teacherId}`)
    return accountsState
  }
  if (!learner || !isLearnerAccount(learner)) {
    logError(`Invalid learner account: ${learnerId}`)
    return accountsState
  }
  const newRelationships: RelationshipGraph = {
    ...accountsState.relationships,
    teacherToLearners: {
      ...accountsState.relationships.teacherToLearners,
      [teacherId]: [
        ...(accountsState.relationships.teacherToLearners[teacherId] || []),
        learnerId,
      ].filter((id, index, self) => self.indexOf(id) === index), // Deduplicate
    },
    learnerToTeachers: {
      ...accountsState.relationships.learnerToTeachers,
      [learnerId]: [
        ...(accountsState.relationships.learnerToTeachers?.[learnerId] || []),
        teacherId,
      ].filter((id, index, self) => self.indexOf(id) === index), // Deduplicate
    },
  }
  return {
    ...accountsState,
    relationships: newRelationships,
  }
}

/**
 * Unlink a parent from a learner (bidirectional).
 * Pure function — returns new AccountsState.
 */
export function unlinkParentFromLearner(
  accountsState: AccountsState,
  parentId: string,
  learnerId: string,
): AccountsState {
  const rels = accountsState.relationships
  return {
    ...accountsState,
    relationships: {
      ...rels,
      parentToLearners: {
        ...rels.parentToLearners,
        [parentId]: (rels.parentToLearners[parentId] || []).filter(
          (id) => id !== learnerId,
        ),
      },
      learnerToParents: {
        ...rels.learnerToParents,
        [learnerId]: (rels.learnerToParents[learnerId] || []).filter(
          (id) => id !== parentId,
        ),
      },
    },
  }
}

/**
 * Unlink a teacher from a learner.
 * Pure function — returns new AccountsState.
 */
export function unlinkTeacherFromLearner(
  accountsState: AccountsState,
  teacherId: string,
  learnerId: string,
): AccountsState {
  const rels = accountsState.relationships
  return {
    ...accountsState,
    relationships: {
      ...rels,
      teacherToLearners: {
        ...rels.teacherToLearners,
        [teacherId]: (rels.teacherToLearners[teacherId] || []).filter(
          (id) => id !== learnerId,
        ),
      },
      learnerToTeachers: {
        ...rels.learnerToTeachers,
        [learnerId]: (rels.learnerToTeachers?.[learnerId] || []).filter(
          (id) => id !== teacherId,
        ),
      },
    },
  }
}
